import type { Fixability, MeasurementStatus, Severity } from './common.js';
import type { NormalizedGeometry } from './geometry.js';
import type { AnalysisBuffer, ImageMeta } from './image.js';
import type { ImageMetadata } from './metadata.js';
import type { BackgroundMetrics, FaceGeometryMetrics, QualityMetrics } from './metrics.js';
import type { FaceObservations } from './observations.js';
import type {
  DocumentProfile,
  MeasurementSpec,
  OfficialBounds,
  PolicySpec,
  ResolvedRule,
} from './profile.js';

export interface FileInfo {
  sizeBytes?: number;
  /** MIME type sniffed from magic bytes — authoritative. */
  detectedMimeType?: string;
  /** MIME type the client claimed — informational only, never trusted. */
  declaredMimeType?: string;
  extension?: string;
  hasExif?: boolean;
  /**
   * Parsed container metadata (EXIF/JFIF/ICC). `undefined` means "the bytes were
   * not available for parsing"; an empty object means "parsed, nothing found".
   * Consumers must keep those two cases apart.
   */
  metadata?: ImageMetadata;
  isAnimated?: boolean;
  /** Decoder reported a width/height it could not confirm against the header. */
  suspiciousHeader?: boolean;
}

/** Everything the checks may look at. */
export interface ValidationContext {
  image: ImageMeta;
  /** Downscaled pixels for quality/background analysis. */
  pixels?: AnalysisBuffer;
  fileInfo: FileInfo;
  faces: FaceObservations;
  geometry?: FaceGeometryMetrics;
  quality?: QualityMetrics;
  background?: BackgroundMetrics;
  /** Injected clock, keeps reports deterministic in tests. */
  now: Date;
}

export interface CheckRunContext extends ValidationContext {
  profile: DocumentProfile;
  rule: ResolvedRule;
}

/** A capability a check needs; missing capability ⇒ honest `not_checked`. */
export type CheckRequirement =
  | 'imageMeta'
  | 'fileInfo'
  | 'metadata'
  | 'pixels'
  | 'faces'
  | 'geometry'
  | 'quality'
  | 'background';

export type CheckKind = 'technical' | 'geometry' | 'quality' | 'background' | 'content';

/**
 * Checklist category shown to the user.
 *
 * The categories are a presentation contract shared by the core, the browser
 * interface and the API: the report stays a flat, document-agnostic list of
 * independent checks, and only this map decides which heading a check appears
 * under. There is deliberately no `print` category — the validator checks a
 * digital file, not a printed sheet.
 */
export type CheckCategory =
  | 'geometry'
  | 'face'
  | 'light'
  | 'quality'
  | 'background'
  | 'technical'
  | 'authenticity';

export const CHECK_CATEGORY_ORDER: readonly CheckCategory[] = [
  'geometry',
  'face',
  'quality',
  'light',
  'background',
  'technical',
  'authenticity',
];

export const CHECK_CATEGORY_TITLES: Record<CheckCategory, string> = {
  geometry: 'Геометрия',
  face: 'Лицо',
  quality: 'Качество изображения',
  light: 'Освещение',
  background: 'Фон',
  technical: 'Файл',
  authenticity: 'Цифровая обработка',
};

/**
 * Which heading every built-in check belongs to.
 *
 * A check that is not listed falls into `technical`, which keeps the interface
 * total (an unknown third-party check is still shown, never dropped).
 */
export const CHECK_CATEGORY: Record<string, CheckCategory> = {
  // Геометрия
  head_size: 'geometry',
  eye_line: 'geometry',
  face_position: 'geometry',
  face_pose: 'geometry',
  head_coverage: 'geometry',
  // Лицо
  face_count: 'face',
  eye_open: 'face',
  mouth: 'face',
  expression: 'face',
  face_obstruction: 'face',
  hair_occlusion: 'face',
  glasses: 'face',
  headwear: 'face',
  // Свет
  exposure: 'light',
  lighting: 'light',
  lighting_balance: 'light',
  shadow_face: 'light',
  shadow_background: 'light',
  highlight: 'light',
  red_eye: 'light',
  // Качество
  sharpness: 'quality',
  noise: 'quality',
  compression_artifacts: 'quality',
  // Фон
  background: 'background',
  background_quality: 'background',
  // Технические параметры
  image_size: 'technical',
  aspect_ratio: 'technical',
  file_format: 'technical',
  file_size: 'technical',
  color_profile: 'technical',
  dpi: 'technical',
  // Digital authenticity
  retouching: 'authenticity',
  ai_manipulation: 'authenticity',
  background_replacement: 'authenticity',
  resampling: 'authenticity',
};

export const categoryOf = (checkId: string): CheckCategory =>
  CHECK_CATEGORY[checkId] ?? 'technical';

/**
 * What the authority requires, versus what we measured.
 *
 * The two are kept apart on purpose: a consumer of the report must be able to
 * tell an official requirement from an internal algorithm bound, and must see
 * how large our measurement uncertainty is.
 */
export interface CheckMeasurement {
  value?: number;
  unit?: string;
  reference?: string;
  /** Absolute uncertainty of `value` in the same unit. */
  tolerance?: number;
}

/** Result of one independent check. */
export interface CheckResult {
  id: string;
  /** Short human title, e.g. «Размер головы». */
  title: string;
  status: MeasurementStatus;
  severity: Severity;
  /** Measured value in `unit` units (mirror of `measurement.value`). */
  value?: number;
  min?: number;
  max?: number;
  unit?: string;
  /** What we measured and how uncertain it is. */
  measurement?: CheckMeasurement;
  /** Official bounds of the document for this check. */
  official?: OfficialBounds;
  /** Internal policy bounds that are NOT an official requirement. */
  policy?: PolicySpec;
  /** What we found — always shown to the user. */
  message: string;
  /** What the requirements say, when it makes sense to restate it. */
  requirement?: string;
  fixability: Fixability;
  /** Shapes the overlay draws, normalized to the image. */
  geometry?: NormalizedGeometry[];
  /** Localized problem regions, normalized. */
  problemRegions?: Array<{
    id: string;
    rect: { x: number; y: number; width: number; height: number };
    message: string;
    severity: 'warning' | 'error';
  }>;
  /** 0..1 — how sure the check is about its own verdict. */
  confidence?: number;
  /** Wall-clock cost of this check, milliseconds (filled by the rule engine). */
  durationMs?: number;
  /** Machine-readable extra data, hidden behind «Подробнее». */
  details?: Record<string, unknown>;
}

export interface ValidationCheck {
  /** Stable id, also the key used in `profile.rules`. */
  id: string;
  title: string;
  description: string;
  kind: CheckKind;
  /** What the check needs; drives both scheduling and honest `not_checked`. */
  requires: CheckRequirement[];
  /**
   * Runs the check. Implementations must be pure: same context ⇒ same result,
   * no I/O, no clock reads (use `ctx.now`), no global state.
   */
  run(ctx: CheckRunContext): CheckResult;
}

/** Helper for reading typed numbers out of a declarative rule. */
export const numberParam = (
  params: Record<string, unknown>,
  key: string,
  fallback: number,
): number => {
  const value = params[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
};

export const optionalNumberParam = (
  params: Record<string, unknown>,
  key: string,
): number | undefined => {
  const value = params[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

export const stringParam = (
  params: Record<string, unknown>,
  key: string,
  fallback: string,
): string => {
  const value = params[key];
  return typeof value === 'string' && value.length > 0 ? value : fallback;
};

export const stringArrayParam = (
  params: Record<string, unknown>,
  key: string,
  fallback: string[],
): string[] => {
  const value = params[key];
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }
  return fallback;
};

export const booleanParam = (
  params: Record<string, unknown>,
  key: string,
  fallback: boolean,
): boolean => {
  const value = params[key];
  return typeof value === 'boolean' ? value : fallback;
};

/**
 * Resolves the bounds a check should enforce.
 *
 * Priority: an explicit `official` block wins; the legacy flat `min`/`max` in
 * `params` is still honoured so third-party profiles keep working. `policy`
 * is returned separately and never silently merged into the official bounds.
 */
export const resolveBounds = (
  rule: ResolvedRule,
): {
  official: OfficialBounds;
  measurement: MeasurementSpec;
  policy?: PolicySpec;
  /** Bounds actually used for the verdict (official, narrowed by policy). */
  effective: OfficialBounds;
} => {
  const official: OfficialBounds = {
    ...(rule.official ?? {}),
    ...(rule.official?.min === undefined && typeof rule.params['min'] === 'number'
      ? { min: rule.params['min'] as number }
      : {}),
    ...(rule.official?.max === undefined && typeof rule.params['max'] === 'number'
      ? { max: rule.params['max'] as number }
      : {}),
    ...(rule.official?.unit === undefined && typeof rule.params['unit'] === 'string'
      ? { unit: rule.params['unit'] as string }
      : {}),
    ...(rule.official?.reference === undefined && typeof rule.params['reference'] === 'string'
      ? { reference: rule.params['reference'] as string }
      : {}),
  };

  const measurement: MeasurementSpec = {
    tolerance:
      rule.measurement?.tolerance ??
      (typeof rule.params['tolerance'] === 'number' ? (rule.params['tolerance'] as number) : 0),
    ...(rule.measurement?.uncertainBelowConfidence !== undefined
      ? { uncertainBelowConfidence: rule.measurement.uncertainBelowConfidence }
      : typeof rule.params['uncertainBelowConfidence'] === 'number'
        ? { uncertainBelowConfidence: rule.params['uncertainBelowConfidence'] as number }
        : {}),
  };

  const policy = rule.policy;
  /**
   * The enforced window is the *intersection* of the official bounds and the
   * internal policy: a policy may tighten a requirement but can never relax an
   * official one.
   */
  const effectiveMin = Math.max(
    official.min ?? Number.NEGATIVE_INFINITY,
    policy?.min ?? Number.NEGATIVE_INFINITY,
  );
  const effectiveMax = Math.min(
    official.max ?? Number.POSITIVE_INFINITY,
    policy?.max ?? Number.POSITIVE_INFINITY,
  );
  const effective: OfficialBounds = {
    ...(Number.isFinite(effectiveMin) ? { min: effectiveMin } : {}),
    ...(Number.isFinite(effectiveMax) ? { max: effectiveMax } : {}),
    ...(official.unit ? { unit: official.unit } : {}),
    ...(official.reference ? { reference: official.reference } : {}),
  };

  return { official, measurement, ...(policy ? { policy } : {}), effective };
};
